import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDivider, MatListModule } from '@angular/material/list';

@Component({
  selector: 'siat-drag-upload-button',
  templateUrl: './drag-upload-button.component.html',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatDivider,
  ],
  styleUrls: ['./drag-upload-button.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DragUploadButtonComponent),
      multi: true,
    },
  ],
})
export class DragUploadButtonComponent implements ControlValueAccessor {
  /**
   * The input label.
   */
  @Input()
  label = 'Carica Documenti';

  /**
   * The input id.
   */
  @Input()
  id: string;

  /**
   * The input name.
   */
  @Input()
  name: string;

  /**
   * A boolean it tells if file input are multiple or not.
   */
  @Input()
  multiple = true;

  /**
   * The accept file type string.
   */
  @Input()
  accept = 'application/pdf,image/jpeg,image/png';

  /**
   * The formgroup.
   */
  @Input()
  formGroup: FormGroup;

  /**
   * The controlname.
   */
  @Input()
  controlName: string;

  /**
   * A reference to the input.
   */
  @ViewChild('fileInput')
  fileInput: ElementRef;

  /**
   * The string for filenames.
   */
  fileNamesDisplay = '';

  /**
   * The files associated to input.
   */
  files: File[] = [];

  /**
   * A boolean for draggin.
   */
  isDragging = false;

  /**
   * A boolean for hide drag.
   */
  @Input()
  hideDrag = false;

  /**
   * @returns {FormControl }
   */
  get control(): FormControl {
    return this.formGroup?.get(this.controlName) as FormControl;
  }

  /**
   * On drag over event.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  /**
   * On drag leave.
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /**
   * On drop file.
   */
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;

    this.isDragging = false;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  /**
   * Open file picker.
   */
  openFilePicker(): void {
    if (this.control?.disabled) return;
    this.fileInput.nativeElement.click();
  }

  /**
   * On selected file.
   */
  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    const files = element.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
    element.value = '';
  }

  /**
   * A method used for build the file names string.
   */
  private updateFileNamesDisplay(): void {
    if (!this.files || this.files.length === 0) {
      this.fileNamesDisplay = '';
    } else if (this.files.length === 1) {
      this.fileNamesDisplay = this.files[0].name;
    } else {
      this.fileNamesDisplay = `${this.files.length} file selezionati`;
    }
  }

  /**
   * Handle files event
   */
  private handleFiles(fileList: FileList): void {
    const newFiles = Array.from(fileList);

    if (this.multiple) {
      const currentFileNames = new Set(this.files.map(f => f.name));
      const uniqueNewFiles = newFiles.filter(
        f => !currentFileNames.has(f.name)
      );
      this.files = [...this.files, ...uniqueNewFiles];
    } else {
      this.files = [newFiles[0]];
    }
    this.updateValue();
  }

  /**
   * Remove file from list.
   */
  removeFile(fileToRemove: File): void {
    this.files = this.files.filter(file => file !== fileToRemove);
    this.updateValue();
  }

  /**
   * Update value.
   */
  private updateValue(): void {
    this.onChange(this.files);
    this.onTouched();
    this.updateFileNamesDisplay();
  }

  /**
   * Format bytes
   * @return { string }
   */
  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Write value.
   */
  writeValue(value: File[] | null): void {
    if (Array.isArray(value)) {
      this.files = value;
    } else {
      this.files = [];
    }
    this.updateFileNamesDisplay();
  }

  /**
   * Keeps a reference to the function to be called every time
   * the value of the field is changed in the UI.
   */
  onChange: (value: File[]) => void = () => {};

  /**
   * Keeps a reference to the function to be called every time
   * the field has to be considered touched (normally on blur).
   */
  onTouched: () => void = () => {};

  /**
   * Register on change.
   */
  registerOnChange(changeCallback: (value: File[]) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * Register on touched.
   */
  registerOnTouched(touchedCallback: () => void) {
    this.onTouched = touchedCallback;
  }
}
